import { faArrowUpFromBracket } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import type { Props as ButtonProps } from '#src/atoms/buttons/Button/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Share } from '#src/atoms/icons/Share.tsx';
import type { IconDefinition } from '#src/atoms/icons/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import type { UTM_CAMPAIGNS } from '#src/hooks/useUtmParams.ts';
import type {
  SocialShareEventContext,
  SocialShareEventName,
} from '#src/lib/events.ts';
import { glossary } from '#src/lib/glossary.ts';

import { SocialShareDialog as Dialog } from './Dialog/index.tsx';

const StyledDialog = styled(Dialog)`
  max-height: 80vh;
`;

type Props = {
  url?: string;
  title?: string;
  description?: string;
  message?: string | ((link: string | undefined) => string);
  image?: {
    post: string | null;
    square: string | null;
    story: string | null;
  } | null;
  trackingEventName: SocialShareEventName;
  trackingEventContext: SocialShareEventContext;
  sharedItemId?: string;
  sharedItem?: UTM_CAMPAIGNS;
  renderButton: (props: {
    onClick: () => void;
    ShareButton: (props: ButtonProps) => ReactNode;
    Icon: ReactNode;
    icon: IconDefinition;
    label: string;
  }) => React.ReactNode;
  onShareClose?: () => void;
  onShareOpen?: () => void;
};

export const SocialShare = (props: Props) => {
  const {
    title,
    description,
    url,
    image,
    message,
    renderButton,
    trackingEventName,
    trackingEventContext,
    sharedItemId,
    sharedItem,
    onShareClose,
    onShareOpen,
  } = props;
  const [opened, setOpened] = useState(false);
  const { formatMessage } = useIntlContext();
  const { isAndroidApp, postMessage } = useIsMobileApp();

  return (
    <>
      {renderButton({
        onClick: () => {
          onShareOpen?.();
          if (isAndroidApp) {
            postMessage('socialShare', {
              title,
              description,
              url,
              image: image
                ? {
                    post: image.post || undefined,
                    story: image.story || undefined,
                    square: image.square || undefined,
                  }
                : undefined,
              message: typeof message === 'function' ? message(url) : message,
              trackingEventName,
              trackingEventContext,
              sharedItemId,
              sharedItem,
            });
          } else {
            setOpened(!opened);
          }
        },
        ShareButton: ({ onClick: onButtonClick, ...rest }) => (
          <Button
            color="tertiary"
            onClick={event => {
              setOpened(!opened);
              onButtonClick?.(event);
              onShareOpen?.();
            }}
            aria-label={formatMessage(glossary.share)}
            {...rest}
          />
        ),
        Icon: <Share />,
        icon: faArrowUpFromBracket,
        label: formatMessage(glossary.share),
      })}
      <StyledDialog
        close={() => {
          setOpened(false);
          onShareClose?.();
        }}
        opened={opened}
        title={title || formatMessage(glossary.share)}
        description={description}
        message={message}
        image={image}
        shareProps={{
          url,
          trackingEventName,
          trackingEventContext,
          sharedItemId,
          sharedItem,
        }}
      />
    </>
  );
};
